import { EventStatus, type MatrixClient, type Room } from 'matrix-js-sdk'
import { getRetryableRoomMessages } from './matrixPendingMessages'

type SendResponse = Awaited<ReturnType<MatrixClient['sendMessage']>>
type MessageContent = Parameters<MatrixClient['sendMessage']>[2]

// Serialize room preparation as well as sending: two callers must not replay
// the same pending event while the first resend is still in flight.
const roomSends = new WeakMap<MatrixClient, Map<string, Promise<SendResponse>>>()

export function findRoomEventForClientTxn(room: Room | null | undefined, txnId: string) {
  if (!room || !txnId.trim()) {
    return undefined
  }
  return room.getEventForTxnId(txnId)
    ?? room.getLiveTimeline().getEvents().find(event =>
      (event.getTxnId() ?? event.getUnsigned().transaction_id) === txnId)
}

async function sendOnce(
  client: MatrixClient,
  roomId: string,
  content: MessageContent,
  txnId: string,
): Promise<SendResponse> {
  const room = client.getRoom(roomId)
  const existing = findRoomEventForClientTxn(room, txnId)
  const eventId = existing?.getId()
  if (existing && eventId && (existing.status === EventStatus.SENT || existing.status === null)) {
    return { event_id: eventId }
  }

  if (room) {
    for (const event of getRetryableRoomMessages(room)) {
      if (event.status !== EventStatus.NOT_SENT) {
        continue
      }
      const response = await client.resendEvent(event, room)
      // This is the queued message itself, not a precondition for a new send.
      if (event.getTxnId() === txnId) {
        return response
      }
    }
  }

  // A send initiated elsewhere in the SDK may still be running. Retain the
  // outbox entry for a later retry instead of creating a second local echo.
  if (findRoomEventForClientTxn(room, txnId)) {
    throw new Error('Сообщение ещё отправляется; повторите попытку позже')
  }

  // Reusing the same txnId also protects retries after a reload or a lost HTTP
  // response, when the previous local echo is no longer available.
  return client.sendMessage(roomId, null, content, txnId)
}

export function sendMatrixTextEvent(
  client: MatrixClient,
  roomId: string,
  content: MessageContent,
  txnId: string,
): Promise<SendResponse> {
  let sends = roomSends.get(client)
  if (!sends) {
    sends = new Map()
    roomSends.set(client, sends)
  }
  const previous = sends.get(roomId) ?? Promise.resolve()
  const send = previous.catch(() => {}).then(() => sendOnce(client, roomId, content, txnId))
  const tracked = send.finally(() => {
    if (sends.get(roomId) === tracked) {
      sends.delete(roomId)
    }
  })
  sends.set(roomId, tracked)
  return tracked
}
